/**
 * Role and Permission Constants for RBAC
 *
 * Target vocabulary: owner / member / viewer (plus the orthogonal
 * is_superuser flag, which is NOT a role). `member` inherits the fuller legacy
 * non-owner permission set (the former manager role); the backend `can.ts` is
 * the authoritative gate and already aliases both legacy non-owner roles →
 * member.
 */

export const ROLES = {
  OWNER: 'owner',
  MEMBER: 'member',
  VIEWER: 'viewer'
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

/**
 * Legacy + canonical role spellings → canonical role.
 *
 * Mirrors the backend `infrastructure/services/agent-harness/src/permissions/can.ts`
 * ROLE_ALIASES: a stale legacy-role JWT (issued before the R1 vocabulary
 * collapse and not yet re-logged-in) must resolve to a real role so
 * `PERMISSION_MAP[role]` and `isMember()`/`isOwner()`/`isViewer()` work.
 */
const ROLE_ALIASES: Readonly<Record<string, Role>> = {
  [ROLES.OWNER]: ROLES.OWNER,
  [ROLES.MEMBER]: ROLES.MEMBER,
  [ROLES.VIEWER]: ROLES.VIEWER,
  // Legacy marketing-era roles collapse into `member` (matches backend can.ts).
  marketing_manager: ROLES.MEMBER,
  content_creator: ROLES.MEMBER,
};

/**
 * Normalize a raw role claim to a canonical Role.
 *
 * Unlike the backend (which returns null for unknown and 401/403s), the
 * frontend never blocks a whole session on an unrecognized claim — any
 * unknown/empty/missing value falls back to `viewer`, the safe
 * least-privilege default, so the UI degrades to read-only rather than
 * rendering blank.
 *
 * @param raw - The raw role value from the JWT / user record
 * @returns A canonical Role (owner / member / viewer)
 */
export function normalizeRole(raw: string | null | undefined): Role {
  if (!raw) {
    return ROLES.VIEWER;
  }
  // `Object.hasOwn`, NOT a bare index (story 3.1 review) — mirrors the backend
  // `can.ts`. A claim of `'constructor'` / `'toString'` resolves through the
  // prototype chain to a FUNCTION, which this function would then return as a
  // `Role`. `canUseTicketWriteVerbs` still fails closed on it, but
  // `hasPermission()` would index `PERMISSION_MAP` with it and throw on
  // `.includes` — a render crash rather than a read-only degrade.
  if (!Object.hasOwn(ROLE_ALIASES, raw)) {
    return ROLES.VIEWER;
  }
  return ROLE_ALIASES[raw]!;
}

export const ROLE_DISPLAY_NAMES: Record<Role, string> = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.MEMBER]: 'Member',
  [ROLES.VIEWER]: 'Viewer'
};

export const PERMISSIONS = {
  USERS_INVITE: 'users.invite',
  USERS_MANAGE: 'users.manage',
  DOCUMENTS_MODIFY_FOUNDATION: 'documents.modify_foundation',
  WORKFLOWS_EXECUTE_ONBOARDING: 'workflows.execute_onboarding',
  WORKFLOWS_EXECUTE: 'workflows.execute',
  CAMPAIGNS_CREATE: 'campaigns.create',
  CAMPAIGNS_EDIT: 'campaigns.edit',
  CONTENT_CREATE: 'content.create',
  CONTENT_EDIT: 'content.edit',
  CALENDAR_VIEW: 'calendar.view',
  ANALYTICS_VIEW: 'analytics.view',
  DOCUMENTS_READ: 'documents.read',
  DASHBOARDS_VIEW: 'dashboards.view',
  // Epic 8 story 8.3 — board curation (create / rename / archive boards,
  // remove / reorder items). Mirrors the harness `dashboards.manage` verb
  // (owner + member), which is the authoritative gate.
  DASHBOARDS_MANAGE: 'dashboards.manage'
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Permission map defining which roles have which permissions
 * Owner has wildcard (*) permission, granting access to all operations.
 * `member` carries the fuller legacy non-owner permission set (former
 * manager role); `viewer` stays read-only.
 */
export const PERMISSION_MAP: Record<Role, Permission[] | ['*']> = {
  [ROLES.OWNER]: ['*'],
  // `member` = the UNION of the two former non-owner roles (marketing_manager
  // ∪ content_creator). CALENDAR_VIEW came from the former content_creator set
  // and must be preserved so a migrated content_creator loses no capability
  // (aligns with the backend granting `member` calendar.read/write).
  [ROLES.MEMBER]: [
    PERMISSIONS.WORKFLOWS_EXECUTE,
    PERMISSIONS.WORKFLOWS_EXECUTE_ONBOARDING,
    PERMISSIONS.CAMPAIGNS_CREATE,
    PERMISSIONS.CAMPAIGNS_EDIT,
    PERMISSIONS.CONTENT_CREATE,
    PERMISSIONS.CONTENT_EDIT,
    PERMISSIONS.CALENDAR_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DASHBOARDS_VIEW,
    PERMISSIONS.DASHBOARDS_MANAGE
  ],
  [ROLES.VIEWER]: [
    PERMISSIONS.DASHBOARDS_VIEW,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.ANALYTICS_VIEW
  ]
};

/**
 * Helper function to check if a role has a specific permission
 * @param role - User's role
 * @param permission - Permission to check
 * @returns True if role has permission, false otherwise
 */
export function hasPermission(role: Role, permission: Permission): boolean {
  // Owner has all permissions
  if (role === ROLES.OWNER) {
    return true;
  }

  const rolePermissions = PERMISSION_MAP[role];

  // Check if role's permission list includes the requested permission
  // Cast to readonly array to satisfy TypeScript when dealing with union types
  return (rolePermissions as readonly (Permission | '*')[]).includes(permission);
}

/**
 * Get all permissions for a given role
 * @param role - User's role
 * @returns Array of permissions for the role
 */
export function getPermissionsForRole(role: Role): Permission[] | ['*'] {
  return PERMISSION_MAP[role];
}

/**
 * ADR-020 story 3.1 — may this role use the TICKET WRITE VERBS at all?
 *
 * Mirrors the backend `can.ts` rows `tasks.create` / `tasks.act` /
 * `tasks.reassign` / `tasks.thread_post`, all of which are `['owner','member']`.
 * `tasks.read` is deliberately NOT covered here: it is all-roles, so a viewer
 * still reads every ticket surface and only the write affordances are withheld.
 *
 * DELIBERATELY NOT IN `PERMISSION_MAP` above. That map is the legacy N8N-era
 * permission-string vocabulary (`content.create`, `campaigns.edit`, …) which is
 * disjoint from the backend verb vocabulary and is NOT ported — folding a
 * `can.ts` verb into it would imply the two are one system, and the next person
 * would go looking for `tasks.act` on the server under a name that does not
 * exist there.
 *
 * 🔴 SUPERUSER DOES NOT BYPASS THIS, and that is the whole subtlety. The backend
 * verb map is role-claim-pure (`is_superuser` is not even a JWT claim), so a
 * platform superuser whose TENANT role is `viewer` is refused by
 * `requirePermission` exactly like any other viewer. Superuser equivalence
 * exists on a different axis — the RELATIONSHIP gate inside `applyTaskAction`,
 * "may this principal act on THIS row" — which is why component code folds
 * `isSuperuser` into `isOwner` for assignee-style checks and must NOT fold it
 * in here. Getting this backwards renders buttons that 403.
 *
 * Unknown / missing roles resolve to `viewer` via `normalizeRole`, i.e. no
 * write affordances — the fail-closed direction, and the same outcome the
 * server produces (403 for unknown, 401 for missing).
 */
export function canUseTicketWriteVerbs(rawRole: string | null | undefined): boolean {
  const role = normalizeRole(rawRole);
  return role === ROLES.OWNER || role === ROLES.MEMBER;
}
